import { FileCheck2, MapPinned, MessageSquareText, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

const ITEMS = [
  {
    icon: FileCheck2,
    title: "Original, not copied",
    body: "Every question is written from scratch around publicly available exam-topic guidance. We never use, copy, or claim to provide real exam questions.",
  },
  {
    icon: MapPinned,
    title: "Built for the state you're testing in",
    body: "We focus on the Illinois state-specific material that generic national prep glosses over, alongside the national concepts and math every candidate needs.",
  },
  {
    icon: MessageSquareText,
    title: "Explanations, not just answers",
    body: "Each question includes a plain-English rationale: why the correct answer is correct and where the common traps are, so practice actually sticks.",
  },
  {
    icon: RefreshCw,
    title: "Kept current",
    body: "We review and expand the question bank over time, and every update is included free for the life of your access.",
  },
];

const SAMPLE_QUESTION = {
  question: "Where must a sponsoring broker's escrow account be maintained?",
  choices: [
    "At any institution the broker prefers, insured or not",
    "Only at an Illinois-chartered bank",
    "At a federally insured depository, whether or not the account bears interest",
    "In an interest-bearing account at a securities brokerage",
  ],
  answer:
    "C. Every escrow account — interest-bearing or non-interest-bearing — must be held at a federally insured depository. The rule does not permit an uninsured institution, is not limited to Illinois-chartered banks, and does not require a securities-brokerage account.",
  source: "68 Ill. Adm. Code 1450.750(i)(4)",
};

export default function QuestionCredibilityBlock({ tone = "light", className = "" }) {
  const isNavy = tone === "navy";

  return (
    <div
      className={cn(
        isNavy ? "text-white" : "text-[var(--ibp-ink)]",
        className
      )}
    >
      <p
        className={cn(
          "text-xs font-semibold uppercase tracking-[0.18em]",
          isNavy ? "text-[#E7BE4A]" : "text-[#B7791F]"
        )}
      >
        How our questions are made
      </p>
      <h2
        className={cn(
          "mt-3 font-[var(--font-heading)] text-2xl font-semibold leading-tight sm:text-3xl",
          isNavy ? "text-white" : "text-[var(--ibp-ink)]"
        )}
      >
        This is what source-tied practice looks like.
      </h2>
      <div
        className={cn(
          "mt-6 rounded-2xl border p-5 sm:p-6",
          isNavy
            ? "border-[color:rgba(231,190,74,0.28)] bg-[color:rgba(231,190,74,0.08)]"
            : "border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-offwhite)]"
        )}
      >
        <p
          className={cn(
            "text-sm font-semibold",
            isNavy ? "text-white" : "text-[var(--ibp-ink)]"
          )}
        >
          {SAMPLE_QUESTION.question}
        </p>
        <ol
          type="A"
          className={cn(
            "mt-3 list-outside space-y-1.5 pl-5 text-sm leading-6",
            isNavy ? "text-white/78" : "text-[color:rgba(11,18,32,0.72)]"
          )}
        >
          {SAMPLE_QUESTION.choices.map((choice) => (
            <li key={choice}>{choice}</li>
          ))}
        </ol>
        <p
          className={cn(
            "mt-4 text-sm leading-6",
            isNavy ? "text-white/82" : "text-[color:rgba(11,18,32,0.78)]"
          )}
        >
          <strong>Answer: </strong>
          {SAMPLE_QUESTION.answer}
        </p>
        <p
          className={cn(
            "mt-2 text-xs",
            isNavy ? "text-white/58" : "text-[color:rgba(11,18,32,0.55)]"
          )}
        >
          Source: {SAMPLE_QUESTION.source}
        </p>
        <p
          className={cn(
            "mt-3 text-xs leading-5",
            isNavy ? "text-white/62" : "text-[color:rgba(11,18,32,0.6)]"
          )}
        >
          Every active question includes the answer, reasoning, and a source
          reference. Illinois-specific items cite the statute, rule, or source
          being tested.
        </p>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {ITEMS.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className={cn(
              "rounded-2xl border p-5",
              isNavy
                ? "border-[color:rgba(231,190,74,0.18)] bg-white/5"
                : "border-[color:rgba(10,27,51,0.12)] bg-white"
            )}
          >
            <Icon
              className={cn(
                "h-5 w-5",
                isNavy ? "text-[#E7BE4A]" : "text-[#B7791F]"
              )}
              aria-hidden="true"
            />
            <h3
              className={cn(
                "mt-3 text-sm font-semibold",
                isNavy ? "text-white" : "text-[var(--ibp-ink)]"
              )}
            >
              {title}
            </h3>
            <p
              className={cn(
                "mt-2 text-sm leading-6",
                isNavy ? "text-white/72" : "text-[color:rgba(11,18,32,0.68)]"
              )}
            >
              {body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
